import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import usePageMeta from "@/hooks/usePageMeta";
import PageHero from "@/components/PageHero";
import Lightbox from "@/components/Lightbox";
import { FadeUp } from "@/components/Reveal";
import { getProducts } from "@/lib/api";

export default function Gallery() {
  usePageMeta(
    "Product Gallery | Krithish Scientific Company",
    "Photo gallery of Krithish Scientific Company equipment — every product photographed from our own printed catalog."
  );
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: getProducts, staleTime: 60_000 });
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = (products || []).map((p) => ({ url: p.image, title: p.name, category: p.line_name }));
  const cats = ["All", ...new Set(items.map((i) => i.category))];
  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);

  const spans = useMemo(
    () => ["md:col-span-2 md:row-span-2", "", "", "md:row-span-2", "", "md:col-span-2", "", "md:row-span-2", "", "", "md:col-span-2 md:row-span-2", ""],
    []
  );

  return (
    <>
      <PageHero
        eyebrow="Product Gallery"
        title="The range, in pictures."
        sub="Every product photographed from our own printed catalog — browse by line and tap any photo to view it larger."
      />

      <section className="bg-white py-20 lg:py-24" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
              {cats.map((c) => (
                <button
                  key={c}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition ${
                    cat === c ? "bg-navy text-white" : "border border-slate-200 text-slate-600 hover:border-safety hover:text-safety"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </FadeUp>

          {filtered.length === 0 ? (
            <p className="mt-12 text-sm text-slate-500">Loading catalog photos…</p>
          ) : (
            <div className="mt-10 grid auto-rows-[150px] grid-cols-2 gap-3 md:auto-rows-[190px] md:grid-cols-4">
              {filtered.map((g, i) => (
                <FadeUp key={g.url + g.title} delay={Math.min(i * 0.03, 0.3)} className={spans[i % spans.length]}>
                  <button
                    onClick={() => setLightbox(i)}
                    data-testid={`gallery-item-${i}`}
                    className="group relative h-full w-full overflow-hidden rounded-xl bg-slate-100 text-left"
                  >
                    <img
                      src={g.url}
                      alt={g.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
                    <span className="absolute bottom-3 left-3 right-3 translate-y-2 text-xs font-medium text-white opacity-0 transition group-hover:translate-y-0 group-hover:opacity-100">
                      {g.title}
                    </span>
                  </button>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>

      <Lightbox
        items={filtered}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNav={(d) => setLightbox((i) => (i == null ? null : (i + d + filtered.length) % filtered.length))}
      />
    </>
  );
}
